/**
 * Sidebar agnostico de router. Cada app pasa `items` con `isActive` calculado
 * (p.ej. desde useLocation o useMatch) y un `renderLink` opcional para envolver
 * con <NavLink> o <Link> segun corresponda.
 *
 * Mobile (≤600px): se transforma en bottom-nav (icon + label vertical, full
 * width abajo del viewport) para no comerse el ancho. Override CSS via
 * @media en globals.css — sin cambio de API.
 */
import type { ReactNode } from 'react';

interface NavItem {
  key: string;
  to: string;
  label: string;
  icon?: ReactNode;
  badge?: string | number;
  isActive?: boolean;
}

interface Props {
  items: NavItem[];
  /** Wrapper opcional: renderiza el link (<Link to=...> de react-router, <a href=...>, etc.). */
  renderLink?: (item: NavItem, children: ReactNode) => ReactNode;
  footer?: ReactNode;
  /**
   * Si está presente, renderiza un botón al final del sidebar para colapsar /
   * expandir. El estilo collapsed (rail icon-only) lo aplica el wrapper de la
   * app vía atributo `data-sidebar-collapsed` — este componente solo emite el
   * toggle. Persistencia de preferencia es responsabilidad del wrapper.
   */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

export function Sidebar({ items, renderLink, footer, collapsed, onToggleCollapsed }: Props) {
  return (
    <nav className="aosenuma-sidebar">
      {items.map((it) => {
        const inner = (
          <span
            className={`aosenuma-sidebar__item${it.isActive ? ' is-active' : ''}`}
            title={it.label}
          >
            {it.icon}
            <span className="aosenuma-sidebar__label">{it.label}</span>
            {it.badge != null && (
              <span className="aosenuma-sidebar__badge">{it.badge}</span>
            )}
          </span>
        );
        return (
          <span key={it.key} className="aosenuma-sidebar__link-wrap">
            {renderLink ? renderLink(it, inner) : <a href={it.to}>{inner}</a>}
          </span>
        );
      })}
      <div className="aosenuma-sidebar__spacer" />
      {footer}
      {onToggleCollapsed && (
        <button
          type="button"
          className="aosenuma-sidebar__toggle"
          onClick={onToggleCollapsed}
          title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          aria-expanded={!collapsed}
        >
          <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {collapsed ? (
              <polyline points="9 18 15 12 9 6" />
            ) : (
              <polyline points="15 18 9 12 15 6" />
            )}
          </svg>
          <span className="aosenuma-sidebar__label">
            {collapsed ? 'Expandir' : 'Colapsar'}
          </span>
        </button>
      )}
    </nav>
  );
}
